import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Testimonial {
  name: string;
  sport: string;
  headline: string;
  quote: string;
}

const DEFAULT_FALLBACK: Testimonial[] = [];

export function Testimonials({
  title,
  subtitle,
  items,
  className = "",
}: {
  title?: string;
  subtitle?: string;
  items?: Testimonial[];
  className?: string;
}) {
  const { t } = useTranslation();
  const resolvedTitle = title ?? t("reviewsAndFaq.testimonials.title");
  const resolvedSubtitle = subtitle ?? t("reviewsAndFaq.testimonials.subtitle");
  const defaultItems = t("reviewsAndFaq.testimonials.items", { returnObjects: true }) as Testimonial[];
  const resolvedItems = items ?? (Array.isArray(defaultItems) ? defaultItems : DEFAULT_FALLBACK);
  return (
    <section aria-labelledby="testimonials-heading" className={`py-20 px-4 ${className}`}>
      <div className="container mx-auto max-w-6xl">
        <h2 id="testimonials-heading" className="text-4xl md:text-5xl font-bold text-center mb-4 text-foreground">
          {resolvedTitle}
        </h2>
        <p className="text-center text-lg text-muted-foreground mb-12 max-w-3xl mx-auto">{resolvedSubtitle}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {resolvedItems.map((it, i) => (
            <Card
              key={i}
              className="bg-gradient-to-br from-card to-primary/5 border-border/40 hover:border-primary/50 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10"
            >
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex gap-1 mb-4" aria-label={t("reviewsAndFaq.testimonials.starsAriaLabel")} role="img">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-5 w-5 fill-primary text-primary" aria-hidden="true" />
                  ))}
                </div>
                <h3 className="text-lg font-bold text-foreground leading-snug mb-3">{it.headline}</h3>
                <p className="text-sm text-muted-foreground mb-6 flex-1 leading-relaxed">"{it.quote}"</p>
                <div className="border-t border-border/40 pt-4">
                  <p className="font-semibold text-foreground text-sm">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.sport}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
